import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { groupBy, sortBy } from 'lodash-es';
import { BackendService, StorageService } from '@shared/services';
import {
  AccountBalancesRaw,
  BusinessAccount,
  BusinessAccountDetailsRaw,
  BusinessAccountItem,
  BusinessAccountLimits,
  BusinessAccountLimitsRaw,
  BusinessAccountLimitUtilisationsRaw,
  BusinessAccountListItem,
  BusinessAccountResponse,
  BusinessAccountUpdate,
  CreateBusinessAccountParams,
  FilterValues,
  IndustryItem,
  IndustryListRaw,
  LimitItem,
  LimitUpdateRequest,
  LimitUpdateResponse,
  PermissionRoles,
  RequestPageParams,
  ServiceAccount,
  ServiceAccountListItem,
  ServiceAccountResponse,
  Beneficiaries,
  BeneficiariesResponse,
  BeneficiaryRequestParams,
  BeneficiaryRaw,
  Beneficiary,
} from '@shared/models';
import { constants } from '@shared/constants';
import { IS_EXTENDED_ERRORS } from '@shared/interceptors';
import { filterCreatedAtDateRange, limitDisplayNames, limitTypes, systemTimeZone } from '@shared/utils';
import { mapBusinessAccountBeneficiaryList, mapBusinessAccountList, mapServiceAccountList } from './business-account-mapping-utils';

@Injectable({
  providedIn: 'root',
})
export class BusinessAccountService {
  backendService = inject(BackendService);

  storageService = inject(StorageService);

  businessAccountListFilter = new BehaviorSubject<FilterValues>({});

  public getBusinessAccounts({
    page,
    size,
    searchString,
    activeFilters,
    sortParams,
  }: RequestPageParams): Observable<BusinessAccountListItem> {
    const pageSize = size || constants.TABLE_ROWS;
    let params = new HttpParams().set('page', `${page || 0}`).set('size', `${pageSize}`);

    if (searchString) {
      params = params.append('search', searchString);
    }

    if (activeFilters) {
      Object.keys(activeFilters).forEach((key) => {
        if (key === 'accountType') {
          const keyValue = activeFilters[key]!.split('=');
          params = params.append(keyValue[0], keyValue[1]);
        } else {
          params = params.append(key, activeFilters[key]!);
        }
      });
    }

    if (sortParams) {
      params = params.append('sort', `${sortParams.key},${sortParams.sortDir}`);
    }

    return this.backendService.get<BusinessAccountResponse>(`/v1/business-accounts`, { params }).pipe(
      map((response) => mapBusinessAccountList(response)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public createBusinessAccount(
    createURL: string,
    businessAccountData: Partial<CreateBusinessAccountParams>,
  ): Observable<BusinessAccountItem> {
    return this.backendService.post<BusinessAccountItem>(
      createURL,
      { ...businessAccountData },
      { context: new HttpContext().set(IS_EXTENDED_ERRORS, true) },
    );
  }

  public getIndustries({
    page,
    size,
    sortParams = { key: 'industryCode', sortProp: 'industryCode', sortDir: 'ASC' },
  }: RequestPageParams): Observable<IndustryItem[]> {
    let params = new HttpParams().set('size', size ?? 30);

    if (page) {
      params = params.append('page', page);
    }

    if (sortParams) {
      params = params.append('sort', `${sortParams.key},${sortParams.sortDir}`);
    }
    return this.backendService.get<IndustryListRaw>(`/v1/industry-categories/industry-sub-categories`, { params }).pipe(
      map(({ content = [] }) =>
        content.map((industry) => ({
          value: industry.industryCode,
          label: industry.name,
          categoryCode: industry.categoryCode,
          groupName: industry.categoryName,
        })),
      ),
    );
  }

  public setBusinessAccountFilterParams(filters: FilterValues): void {
    this.storageService.setItem(constants.KOR_BUSINESS_ACCOUNT_FILTER, filters);
    this.updateBusinessAccountFilterParams(filters);
  }

  public getBusinessAccountFilterParams(): void {
    const filters = this.storageService.getItem<FilterValues>(constants.KOR_BUSINESS_ACCOUNT_FILTER);

    if (filters && Object.keys(filters).length) {
      this.updateBusinessAccountFilterParams(filters);
    }
  }

  private updateBusinessAccountFilterParams(filters: FilterValues): void {
    this.businessAccountListFilter.next(filters);
  }

  getBusinessAccountById({ businessAccountId }: { businessAccountId: string }): Observable<BusinessAccountDetailsRaw> {
    return this.backendService.get<BusinessAccountDetailsRaw>(`/v1/business-accounts/${businessAccountId}`);
  }

  getBusinessAccountBalances(businessAccountId: string): Observable<AccountBalancesRaw> {
    return this.backendService.get<AccountBalancesRaw>(`/v1/financial-account-holders/${businessAccountId}/account-balances`);
  }

  updateBusinessAccount({
    data,
    businessAccountId,
  }: {
    data: Partial<BusinessAccountUpdate>;
    businessAccountId: BusinessAccount['id'];
  }): Observable<BusinessAccount> {
    return this.backendService.update<BusinessAccount>(`/v1/business-accounts/${businessAccountId}`, data);
  }

  getBusinessAccountLimits({ businessAccountId }: { businessAccountId: BusinessAccount['id'] }): Observable<BusinessAccountLimits> {
    return this.backendService.get<BusinessAccountLimitsRaw>(`/v1/business-accounts/${businessAccountId}/limits/all`).pipe(
      switchMap((limitConfigs) =>
        this.backendService
          .get<BusinessAccountLimitUtilisationsRaw>(`/v1/business-accounts/${businessAccountId}/limits`)
          .pipe(map((limitUtilisations) => ({ limitConfigs, limitUtilisations }))),
      ),
      map(({ limitConfigs, limitUtilisations }) => {
        const limits = limitConfigs.limits;
        const utilisation = limitUtilisations.periods;
        const result: LimitItem[] = sortBy(
          limits.map((limitItem) => {
            const limitUtilisation = utilisation.filter(
              (utilisationItem) => utilisationItem.type === limitItem.limitType && utilisationItem.amount[limitItem.currency],
            );
            const used = limitUtilisation?.[0].amount[limitItem.currency].used ?? 0;
            const progress = used ? (used / limitItem.amount) * 100 : 0;
            return {
              ...limitItem,
              progress,
              label: limitDisplayNames[limitItem.limitType],
              used,
            };
          }),
          (item) => limitTypes.indexOf(item.limitType),
        );

        return groupBy(result, 'currency');
      }),
    );
  }

  updateTransactionLimits({
    businessAccountId,
    limits,
    currency,
  }: {
    businessAccountId: BusinessAccount['id'];
    limits: LimitUpdateRequest;
    currency: LimitItem['currency'];
  }) {
    return this.backendService.update<LimitUpdateResponse[]>(
      `/v1/business-accounts/${businessAccountId}/limits?currency=${currency}`,
      limits,
      {
        context: new HttpContext().set(IS_EXTENDED_ERRORS, true),
      },
    );
  }

  createServiceAccount({
    serviceAccountData,
  }: {
    serviceAccountData: {
      businessAccountId: string;
      name: string;
      roles: { name: PermissionRoles }[];
    };
  }): Observable<ServiceAccount> {
    return this.backendService.post<ServiceAccount>(`/v1/service-accounts`, serviceAccountData, {
      context: new HttpContext().set(IS_EXTENDED_ERRORS, true),
    });
  }

  updateServiceAccount({
    serviceAccountId,
    serviceAccountData,
  }: {
    serviceAccountId: string;
    serviceAccountData: {
      name: string;
      roles: { name: PermissionRoles }[];
    };
  }): Observable<ServiceAccount> {
    return this.backendService.update<ServiceAccount>(`/v1/service-accounts/${serviceAccountId}`, serviceAccountData, {
      context: new HttpContext().set(IS_EXTENDED_ERRORS, true),
    });
  }

  updateServiceAccountStatus(serviceAccountId: string, action: string): Observable<ServiceAccount> {
    return this.backendService.update<ServiceAccount>(`/v1/service-accounts/${serviceAccountId}/${action}`);
  }

  resetServiceAccount(serviceAccountId: string): Observable<ServiceAccount> {
    return this.backendService.update<ServiceAccount>(`/v1/service-accounts/${serviceAccountId}/reset-api-key`, {
      context: new HttpContext().set(IS_EXTENDED_ERRORS, true),
    });
  }

  deleteServiceAccount(serviceAccountId: string): Observable<ServiceAccount> {
    return this.backendService.delete<ServiceAccount>(`/v1/service-accounts/${serviceAccountId}`);
  }

  getServiceAccounts({ page, size, searchString, sortParams, activeFilters }: RequestPageParams): Observable<ServiceAccountListItem> {
    const pageSize = size || constants.TABLE_ROWS;
    let params = new HttpParams().set('page', `${page || 0}`).set('size', `${pageSize}`);

    if (sortParams) {
      params = params.append('sort', `${sortParams.key},${sortParams.sortDir}`);
    }

    if (searchString) {
      params = params.append('search', searchString);
    }

    if (activeFilters) {
      Object.keys(activeFilters).forEach((key) => {
        if (key === 'createdAt') {
          const { from, to } = filterCreatedAtDateRange(activeFilters[key]) ?? {};

          if (!from || !to) {
            return;
          }

          params = params.append('createdDateFrom', from);
          params = params.append('createdDateTo', to);
          params = params.append('timeZone', systemTimeZone);
        } else if (key === 'updatedAt') {
          const { from, to } = filterCreatedAtDateRange(activeFilters[key]) ?? {};

          if (!from || !to) {
            return;
          }

          params = params.append('updatedDateFrom', from);
          params = params.append('updatedDateTo', to);
          params = params.append('timeZone', systemTimeZone);
        } else {
          params = params.append(key, activeFilters[key]!);
        }
      });
    }

    return this.backendService.get<ServiceAccountResponse>(`/v1/service-accounts`, { params }).pipe(
      map((response) => mapServiceAccountList(response)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  getServiceAccountById({ serviceAccountId }: { serviceAccountId: string }): Observable<ServiceAccount> {
    return this.backendService.get<ServiceAccount>(`/v1/service-accounts/${serviceAccountId}`).pipe(
      map((serviceAccount) => {
        const serviceAccountRoles: string[] = [];
        serviceAccount.roles?.forEach((role) => serviceAccountRoles.push(role.name));

        return { ...serviceAccount, role: serviceAccountRoles.join(',') };
      }),
    );
  }

  updateBusinessAccountStatus(businessAccountId: string, action: string): Observable<BusinessAccount> {
    return this.backendService.update<BusinessAccount>(`/v1/business-accounts/${businessAccountId}/${action}`, undefined, {
      context: new HttpContext().set(IS_EXTENDED_ERRORS, true),
    });
  }

  updateBusinessAccountState(businessAccountId: string, state: string): Observable<BusinessAccount> {
    return this.backendService.update<BusinessAccount>(
      `/v1/business-accounts/${businessAccountId}/state`,
      { state },
      { context: new HttpContext().set(IS_EXTENDED_ERRORS, true) },
    );
  }

  getBeneficiaries(parentId: string): Observable<Beneficiaries[]> {
    const buildUrl = (size: number) => `/v1/beneficiaries?parentId=${parentId}&parentType=BUSINESS_ACCOUNT&size=${size}`;

    return this.backendService.get<BeneficiariesResponse>(buildUrl(50)).pipe(
      switchMap((response) => {
        if (!response.hasNext) return of(response);
        return this.backendService.get<BeneficiariesResponse>(buildUrl(response.totalElements));
      }),
      map((response) => mapBusinessAccountBeneficiaryList(response)),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public addBeneficiary(businessAccountId: string, beneficiaryData: BeneficiaryRequestParams): Observable<BeneficiaryRaw> {
    return this.backendService
      .post<BeneficiaryRaw>(`/v1/business-accounts/${businessAccountId}/beneficiaries`, beneficiaryData)
      .pipe(catchError((errorRes) => throwError(() => errorRes)));
  }

  public getBeneficiaryById(businessAccountId: string, beneficiaryId: string): Observable<Beneficiary> {
    return this.backendService.get<BeneficiaryRaw>(`/v1/business-accounts/${businessAccountId}/beneficiaries/${beneficiaryId}`).pipe(
      map((raw) => ({
        ...raw,
        fullName: `${raw.firstName}${raw.middleName ? ' ' + raw.middleName : ''} ${raw.lastName}`,
        email: raw.primaryEmail?.value,
        phoneNumber: raw.primaryPhoneNumber?.number,
        placeOfBirthDisplayValue: [raw.placeOfBirth?.city, raw.placeOfBirth?.countryCode].filter(Boolean).join(', '),
      })),
      catchError((errorRes) => throwError(() => errorRes)),
    );
  }

  public updateBeneficiary(businessAccountId: string, beneficiaryId: string, data: BeneficiaryRequestParams): Observable<BeneficiaryRaw> {
    return this.backendService
      .update<BeneficiaryRaw>(`/v1/business-accounts/${businessAccountId}/beneficiaries/${beneficiaryId}`, data)
      .pipe(catchError((errorRes) => throwError(() => errorRes)));
  }

  public updateBeneficiaryStatus(beneficiaryId: string, action: string, businessAccountId?: string): Observable<void> {
    const path = businessAccountId
      ? `/v1/business-accounts/${businessAccountId}/beneficiaries/${beneficiaryId}/${action}`
      : `/v1/beneficiaries/${beneficiaryId}/${action}`;
    return this.backendService.update<void>(path, {}).pipe(catchError((errorRes) => throwError(() => errorRes)));
  }

  public deleteBeneficiary(beneficiaryId: string, businessAccountId?: string): Observable<void> {
    const query = businessAccountId ? `?businessAccountId=${businessAccountId}` : '';
    return this.backendService
      .delete<void>(`/v1/beneficiaries/${beneficiaryId}${query}`)
      .pipe(catchError((errorRes) => throwError(() => errorRes)));
  }

  exchangeApiKey(businessAccountId: string): Observable<{ access_token: string }> {
    return this.createServiceAccount({
      serviceAccountData: {
        businessAccountId,
        name: `token-exchange-${Date.now()}`,
        roles: [{ name: 'Admin' as PermissionRoles }],
      },
    }).pipe(
      switchMap((serviceAccount) => {
        const credentials = btoa(`${serviceAccount.apiKeyId}:${serviceAccount.apiKeySecret}`);
        return this.backendService.post<{ access_token: string }>(
          `/v1/api-keys/exchange`,
          {},
          {
            headers: new HttpHeaders({ Authorization: `Basic ${credentials}` }),
          },
        );
      }),
    );
  }
}
